// The body sections of `/fields` (§2–§9). Split out of fields.tsx purely for size; it is the only importer.
// Node-prerender-safe, same rule as splash-chrome.tsx: MUI + react-router + local literals only.
// Every factual claim here is code-anchored — lanes/types/timezone from docs/gnothi/fields/INDEX.md, PCMCI+ params + per-lane defaults from fields/AUDIT-2026-03.md, the Q&A and causal-narrative flows from docs/gnothi/ml-pipeline.md (Flows D and E), the shortcuts from the ✅ rows of Fields/Nodes/KB_SHORTCUTS.md (the rest of that table is unimplemented parity tracking), the fields→Series seam from services/routes/stories/generate.ts. Don't loosen a sentence past its anchor.
// §4's ELIGIBILITY: causal analysis is credit-metered, NOT payment-gated — `services/ml/influencers.ts computeAllInfluencers`'s SQL reads no `has_paid`, its predicate mirrors `aiActive` (credits + consent, or is_cool/is_superuser), and `Fields/Analyze/DataQualityBanner.tsx:50` says so in as many words ("axis B — mirrors aiActive, not has_paid"). `fields/INDEX.md` and `features.md` now say so too; don't let "paid" back into any of them. The callout's three bars are `computeInfluencers`'s own early returns: `userFields.length < 2` (analysis-enabled fields), `entries.length < 5`, and `MIN_FIELD_DAYS = 7` (a field with less history than that is filtered out before the matrix is built). That card's LAST sentence is a required regulatory caveat, not flab: unqualified, "find out what actually moved your mood/sleep" reads to a lay user as a medical finding (FTC deception-by-implication), and the sanctioned wording is a statistical read, never a clinical one — the dom-test ban regex cannot see its absence, so never trim it to tighten the card.
import { Link as RouterLink } from 'react-router';
import Box from '@mui/material/Box';
import Stack from '@mui/material/Stack';
import Grid from '@mui/material/Grid';
import Card from '@mui/material/Card';
import Button from '@mui/material/Button';
import Typography from '@mui/material/Typography';
import RepeatOutlined from '@mui/icons-material/RepeatOutlined';
import TodayOutlined from '@mui/icons-material/TodayOutlined';
import CheckBoxOutlined from '@mui/icons-material/CheckBoxOutlined';
import RedeemOutlined from '@mui/icons-material/RedeemOutlined';
import InsightsOutlined from '@mui/icons-material/InsightsOutlined';
import SplitscreenOutlined from '@mui/icons-material/SplitscreenOutlined';
import TouchAppOutlined from '@mui/icons-material/TouchAppOutlined';
import HubOutlined from '@mui/icons-material/HubOutlined';
import QueryStatsOutlined from '@mui/icons-material/QueryStatsOutlined';
import AccountTreeOutlined from '@mui/icons-material/AccountTreeOutlined';
import AttachFileOutlined from '@mui/icons-material/AttachFileOutlined';
import EventOutlined from '@mui/icons-material/EventOutlined';
import CloudUploadOutlined from '@mui/icons-material/CloudUploadOutlined';
import EmojiEventsOutlined from '@mui/icons-material/EmojiEventsOutlined';
// MenuBook rather than AutoStories: MUI bakes the icon name into a `data-testid` that survives the production build, and A13 bans the string "Stories" from this surface.
import MenuBookOutlined from '@mui/icons-material/MenuBookOutlined';
import { Band, SectionHeader, SectionBody, FeatureCard, accentColor, useOnDark } from './splash-sections';

const LANES = [
  { title: 'Habit', icon: <RepeatOutlined />, body: 'Behaviors you want more or less of, scored the moment you log them. Pushups, or doomscrolling.' },
  { title: 'Daily', icon: <TodayOutlined />, body: 'Do it once per period. You earn on the check and take a penalty on a period you miss. Stretch, floss.' },
  { title: 'To-Do', icon: <CheckBoxOutlined />, body: 'One-and-done, or recurring on a schedule. Left undone it decays and goes redder. Groceries and taxes; rent every month.' },
  { title: 'Reward', icon: <RedeemOutlined />, body: 'Spend what the rest of it earned you, gold first. Ice cream, a night of games.' },
  { title: 'Custom', icon: <InsightsOutlined />, body: 'Data only - no scoring, no game. Weight, sleep, screen time. This is the lane the analysis loves most.' },
] as const;

const TYPES = [
  ['Five-star', 'a 0–5 rating, for the things you would rate rather than count'],
  ['Checkbox', 'did it, or did not'],
  ['Number', '+/− buttons and a text input, for reps, hours, dollars, pounds'],
  ['Option', 'your own list of choices'],
  ['Notes', 'no value at all - this field exists only for its outline'],
] as const;

const SHORTCUTS = [
  ['Structure', 'Tab / Shift+Tab to indent and outdent · Alt+Shift+↑/↓ to move a node · Enter splits into a new sibling · Shift+Enter adds a note'],
  ['Navigation', 'Alt+↓ zooms into a node and Alt+↑ zooms back out · Ctrl+↓/↑ expand and collapse · ↑/↓ walk the outline'],
  ['In bulk', 'Shift+↑/↓ or Shift+click selects a range, then indent, outdent or delete the whole selection at once · Ctrl+O shows or hides completed nodes'],
  ['Harvest', 'Ctrl+Enter checks a node off · Ctrl+Shift+Enter completes it and scores its parent field in the same keystroke'],
] as const;

/** §4's three steps. `tau_max=2` and the hourly cadence are the AUDIT/architecture anchors; "saved back as an entry" is `computeInfluencers`'s causal-summary write. */
const PIPELINE = [
  ['Pivot', 'Your logged entries become a time series - days down one axis, fields across the other.'],
  ['Run PCMCI+', 'The tigramite implementation, on its own Python worker, hourly, looking for effects up to two days lagged.'],
  ['Write it down', 'The strongest links get summarized in a few plain sentences and saved back into your journal as an entry - something to read, not a chart to interpret.'],
] as const;

/** §5's example questions. The shape is what `ml/tableqa.ts` actually answers: your own fields and values as tables, with each field's influencer score attached. */
const QUESTIONS = [
  'What happened to my focus in the weeks I stopped lifting?',
  'Which of my fields is pulling the most weight right now?',
  'Is my sleep actually improving, or is that just noise?',
] as const;

/** §8's four restraints, as `lead + rest` rows. Sentence-final periods live in the lead, hence `sep=" "` rather than the ` - ` gloss the other two lists take. */
const RESTRAINTS = [
  ['Damage is flat.', "An overdue todo costs a small fixed amount of experience per day, with no scaling for how overdue it is - so the todo you forgot two months ago can't wipe you out."],
  ['Partial credit is proportional.', 'Miss a quota and the penalty is the fraction you missed, capped at one per field. Three of six done is half a penalty, not three.'],
  ['The loss multiplier is 1×.', 'Losses are counted once, not amplified, and a todo with no due date costs you no experience at all - it just goes visibly redder.'],
  ["Scoring is per-field, and off where it doesn't belong.", 'The Custom lane ships with scoring off and the To-Do lane with analysis off, so you can track weight, sleep or screen time with zero game attached to it.'],
] as const;

/** The file's three `bold lead + rest` lists (input types, keyboard shortcuts, restraints) render through here — six hand-rolled copies of this row was the pattern before. `useOnDark` and not a bare `text.primary`: that token is near-black on the navy wash, so hard-coding it is what makes a section dark-band-unsafe BY CONSTRUCTION (splash-sections' OnDark note). */
function Rows({ rows, variant = 'body2', sep = ' - ', spacing = 1.25 }: { rows: readonly (readonly [string, string])[]; variant?: 'body1' | 'body2'; sep?: string; spacing?: number }) {
  const onDark = useOnDark();
  return (
    <Stack spacing={spacing}>
      {rows.map(([lead, rest]) => (
        <Typography key={lead} variant={variant}>
          <Box component="span" sx={{ fontWeight: 700, color: onDark ? 'common.white' : 'text.primary' }}>{lead}</Box>{sep}{rest}
        </Typography>
      ))}
    </Stack>
  );
}

export function Lanes() {
  return (
    <Band id="lanes" color="light">
      <SectionHeader
        icon={<SplitscreenOutlined />}
        tone="behavior"
        title="Five lanes, one system"
        deck="A field's lane decides its game mechanics - when it scores, when it resets, and what happens when you ignore it."
      />
      <Grid container spacing={2} sx={{ justifyContent: 'center' }}>
        {LANES.map((lane) => (
          <Grid size={{ xs: 12, sm: 6, md: 4 }} key={lane.title}>
            <FeatureCard title={lane.title} icon={lane.icon} tone="behavior">{lane.body}</FeatureCard>
          </Grid>
        ))}
      </Grid>
    </Band>
  );
}

export function InputTypes() {
  return (
    <Band id="log" color="grey">
      <SectionHeader
        icon={<TouchAppOutlined />}
        tone="behavior"
        title="Log it in about a second"
        deck="Five input types, because the friction of logging is the whole game. Nothing to analyze if you never write it down."
      />
      <SectionBody>
        <Rows rows={TYPES} variant="body1" spacing={1.5} />
      </SectionBody>
      <SectionBody>
        Forgot yesterday? Step the day back and log it there - every field, every value, on any day you choose. And all of it is anchored to <em>your</em> local midnight, in your own timezone, rather than to a server day that rolls over mid-evening.
      </SectionBody>
    </Band>
  );
}

export function Causal() {
  return (
    <Band id="causal" color="dark">
      <SectionHeader
        icon={<HubOutlined />}
        tone="prompt"
        title="Your behavior, as a causal graph"
        deck="Not a streak badge. A directed graph of which of your tracked fields actually move the others."
      />
      <SectionBody center>
        This is the part of an AI habit tracker that a wall of streaks can't do - a causal claim, not a correlation heatmap.
      </SectionBody>
      {/* The numeral takes `primary.main` rather than an opacity step: the accent is ~7:1 on the navy wash, where a dimmed white reads as disabled. `component` is spelled on both, same reason `FeatureCard` does it — a bare `variant="h5"`/`"h6"` renders a real <h5>/<h6> and would skip two levels under this band's <h2>. */}
      <Grid container spacing={3}>
        {PIPELINE.map(([title, body], i) => (
          <Grid size={{ xs: 12, md: 4 }} key={title}>
            <Stack spacing={0.75}>
              <Typography variant="h5" component="div" sx={{ fontWeight: 700, color: 'primary.main' }}>{i + 1}</Typography>
              <Typography variant="h6" component="h3">{title}</Typography>
              <Typography variant="body2" sx={{ opacity: 0.8 }}>{body}</Typography>
            </Stack>
          </Grid>
        ))}
      </Grid>
      <SectionBody>
        {/* `color` is not optional here: `MuiCard-root` sets `color:var(--mui-palette-text-primary)` (#0f172a), so this callout's eligibility bar rendered near-black on the navy wash — invisible to typecheck, to the DOM suite and to every artifact grep. */}
        <Card variant="outlined" sx={{ p: 2, borderRadius: 3, bgcolor: 'rgba(255,255,255,0.06)', borderColor: 'rgba(255,255,255,0.25)', color: 'common.white' }}>
          <Typography variant="body2" sx={{ fontWeight: 600, mb: 0.5 }}>Before you sign up expecting this on day one</Typography>
          <Typography variant="body2">
            Causal analysis runs on AI credits - every account starts with a balance - and it needs at least two analysis-enabled fields and at least five entries before it can run at all. A field also needs roughly a week of its own history before it counts toward the graph. Tracking itself is free, and the graph gets sharper the longer you log. What comes out is a statistical read of the fields you tracked - not a medical finding or clinical guidance.
          </Typography>
        </Card>
      </SectionBody>
    </Band>
  );
}

export function AskYourData() {
  return (
    <Band id="ask" color="light">
      <SectionHeader
        icon={<QueryStatsOutlined />}
        tone="prompt"
        title="Ask your data anything"
        deck={'"Which days do I sleep worst, and what did I do the day before?"'}
      />
      <SectionBody center>
        Your fields and their values go to the model as tables, with each field's influencer score attached, and the answer comes back in plain language - citing the specific dates, values and trends it read, and naming the direction and size of what it found.
      </SectionBody>
      <Grid container spacing={2} sx={{ justifyContent: 'center' }}>
        {QUESTIONS.map((q) => (
          <Grid size={{ xs: 12, md: 4 }} key={q}>
            <Card variant="outlined" sx={{ height: '100%', p: 2, borderRadius: 3 }}>
              <Typography variant="body1" sx={{ fontStyle: 'italic' }}>"{q}"</Typography>
            </Card>
          </Grid>
        ))}
      </Grid>
      <SectionBody center>
        When there isn't enough data to answer, it says so. That is deliberate: a confident answer off nine days of logs would be the single easiest thing here to fake.
      </SectionBody>
    </Band>
  );
}

export function Outline() {
  return (
    <Band id="outline" color="grey">
      <SectionHeader
        icon={<AccountTreeOutlined />}
        tone="organization"
        title="A Workflowy-grade outline under every field"
        deck="The outliner and the tracker are the same object - which is the one thing a standalone outliner can't do."
      />
      <SectionBody>
        Every field carries an infinitely nested node tree, driven from the keyboard. The <code>notes</code> field type exists purely for it, for the reference material and project notes that were never going to be a number.
      </SectionBody>
      <SectionBody>
        <Rows rows={SHORTCUTS} />
      </SectionBody>
      <SectionBody>
        Nothing has to be retyped to get in here. Paste an indented block or a bulleted list from anywhere and it lands as a real tree with its nesting intact, and a node that turns out to belong under a different field moves there with everything underneath it.
      </SectionBody>
      <SectionBody>
        Harvest is the seam. In an outliner, finishing a line is bookkeeping. Here it is a logged data point on the field above it - so the notes you were keeping anyway become the dataset the causal graph runs on.
      </SectionBody>
    </Band>
  );
}

export function DatesAndFiles() {
  return (
    <Band id="files" color="light">
      <SectionHeader
        icon={<AttachFileOutlined />}
        tone="organization"
        title="Due dates and attachments"
        deck="Table stakes, present and out of the way - but doing a little more work than usual."
      />
      <Grid container spacing={2} sx={{ justifyContent: 'center' }}>
        {/* Both cards are code-anchored: labels and the red/amber/green bands from `shared/utils/fieldActive.ts` (`dueDateInfo`, `dueDateColors`), the ordering from `fieldUrgencySort.ts`'s buckets, the >3-day drop-off from `isFieldActive`, and the 10MB ceiling from `schemas/fieldNodes.ts`'s `MAX_FILE_SIZE` — whose own comment reads "essential documents, not general file storage". */}
        <Grid size={{ xs: 12, sm: 6 }}>
          <FeatureCard title="A due date that triages itself" icon={<EventOutlined />} tone="behavior">
            Put a date on a todo, or on any single node in an outline. Gnothi labels it in words - due today, due in 2d, 3d overdue - and colors it red once it lands, amber inside three days, green beyond that. Today's list sorts the overdue to the top, and a todo further than three days out stays off it entirely until it's close.
          </FeatureCard>
        </Grid>
        <Grid size={{ xs: 12, sm: 6 }}>
          <FeatureCard title="A file on any node" icon={<CloudUploadOutlined />} tone="organization">
            Attach a file to a node and download it again whenever you need it. The upload goes straight from your browser to storage over a signed URL, so the bytes never travel through the app, and the ceiling is 10MB on purpose: this is for the document a note is about, not a drive to fill.
          </FeatureCard>
        </Grid>
      </Grid>
    </Band>
  );
}

export function Gamification() {
  return (
    <Band id="gamification" color="grey">
      <SectionHeader
        icon={<EmojiEventsOutlined />}
        tone="behavior"
        title="Gamification, deliberately dialed down"
        deck="The points exist to get you logging. The logging exists to feed the causal graph."
      />
      <SectionBody>
        Gnothi comes from the creator of Habitica, so the restraint here is a choice rather than an oversight. Four of them, specifically:
      </SectionBody>
      <SectionBody>
        <Rows rows={RESTRAINTS} sep=" " />
      </SectionBody>
      <SectionBody>
        Optimizing hard for streaks gets you a streak. We'd rather you end up with a dataset worth analyzing, so the penalties stay mild on purpose. There's a Habitica sync if you're keeping both, and{' '}
        <Box component={RouterLink} to="/community" sx={{ color: accentColor(), fontWeight: 600 }}>a group to compare notes with</Box>{' '}if accountability is what keeps you logging.
      </SectionBody>
    </Band>
  );
}

export function SeriesSeam() {
  return (
    <Band id="series" color="dark">
      <SectionHeader
        icon={<MenuBookOutlined />}
        tone="themes"
        title="Your fields write your Series"
        deck="What you track becomes context the author of your Series can actually use."
      />
      <SectionBody center>
        Because that causal summary is saved as an entry, it rides along into chapter generation as a block of field analysis. A Series can know that your sleep collapsed the week you stopped writing, and write the chapter accordingly.
      </SectionBody>
      <SectionBody center>
        Field analysis reaches the author only for a whole-journal Series. A Series you've scoped to particular tags never sees it - that suppression is on purpose, so a narrow Series can't leak the parts of your journal you left out of it.
      </SectionBody>
      <SectionBody center>
        <Button component={RouterLink} to="/series#podcast-generator" variant="outlined" color="inherit" size="large">
          See what it does with it
        </Button>
      </SectionBody>
    </Band>
  );
}
